import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import AADService from "../../funcionesAuth";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import Table from "@mui/material/Table";
import Box from "@mui/material/Box";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import { Checkbox } from "@mui/material";
import Select from "react-select";
import { Snackbar } from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";

const getRiskColor = (nivel) => {
  switch (nivel) {
    case "Tolerable":
      return "green";
    case "Moderado":
      return "orange";
    case "Crítico":
      return "red";
    case "Muy Crítico":
      return "#8B0000";
    default:
      return "gray";
  }
};

function formatNumber(value) {
  const num = Number(value);
  if (isNaN(num)) return "-";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const useStylesModal = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  container: {
    maxHeight: "55vh",
    minHeight: "55vh",
  },
}));

const stylesSelect = {
  control: (base) => ({
    ...base,
    fontFamily: "Roboto",
  }),
  menu: (base) => ({
    ...base,
    fontFamily: "CIBFont Sans Regular",
  }),

  option: (provided, state) => ({
    ...provided,
  }),
};

const getMitigacionValue = (item) => {
  if (item.efectividadcontrolmanual !== 0) {
    return item.efectividadcontrolmanual;
  } else if (item.efectividadcontrol_lb !== 0) {
    return item.efectividadcontrol_lb;
  } else {
    return item.efectividadcontrol;
  }
};

const getGradientColor = (value) => {
  const red = Math.round(255 * (1 - value));
  const green = Math.round(255 * value);
  return `rgb(${red}, ${green}, 0)`;
};

function agruparActivosPorEfectoYFuente({
  item,
  opcionesActivos,
  relacionesAcis,
  resultadoFinal,
  fuentes = null,
}) {
  const activosById = new Map(opcionesActivos.map((a) => [a.value, a]));
  const fuentesPorIdefecto = new Map();
  for (const ef of resultadoFinal) {
    const id = ef.idefecto;
    const fte = normalizarFuente(ef.fuente);
    if (!fuentesPorIdefecto.has(id)) fuentesPorIdefecto.set(id, new Set());
    if (!fuentes || fuentes.includes(fte)) {
      fuentesPorIdefecto.get(id).add(fte);
    }
  }

  const salida = {};
  for (const [id, setFuentes] of fuentesPorIdefecto.entries()) {
    salida[String(id)] = {};
    const fuentesAUsar =
      setFuentes.size > 0 ? Array.from(setFuentes) : fuentes ?? [];
    for (const fte of fuentesAUsar) {
      const fNorm = normalizarFuente(fte);
      salida[String(id)][fNorm] = [];
    }
  }

  const vistos = new Map();
  for (const rel of relacionesAcis) {
    if (rel.idriesgo_id !== item.idriesgo) continue;
    if (rel.estado !== undefined && rel.estado !== 1) continue;

    const idEf = rel.idefecto;
    const fteRel = rel.fuente;
    if (!fuentesPorIdefecto.has(idEf)) continue;

    const setFuentesEsperadas = fuentesPorIdefecto.get(idEf);
    const fuenteValida = fuentes
      ? fuentes.includes(fteRel)
      : setFuentesEsperadas.has(fteRel);
    if (!fuenteValida) continue;
    const activo = activosById.get(rel.idactivo);
    if (!activo) continue;

    const keyEf = String(idEf);
    if (!vistos.has(keyEf)) vistos.set(keyEf, new Map());
    if (!vistos.get(keyEf).has(fteRel))
      vistos.get(keyEf).set(fteRel, new Set());

    const setVistos = vistos.get(keyEf).get(fteRel);
    if (!setVistos.has(activo.value)) {
      salida[keyEf][fteRel].push(activo);
      setVistos.add(activo.value);
    }
  }
  return salida;
}

const normalizarFuente = (fuente) => {
  if (!fuente) return "Local";
  const f = fuente.trim().toLowerCase();
  if (["negocio", "neg", "biz"].includes(f)) return "Negocio";
  if (["local", "loc", "propio", "propios", "prop"].includes(f)) return "Local";
  return "Local";
};

const getValueForRow = (activosSeleccionados, row) => {
  const id = String(row.idefecto);
  const fuenteLectura = normalizarFuente(row.fuente);

  return Array.isArray(activosSeleccionados?.[id]?.[fuenteLectura])
    ? activosSeleccionados[id][fuenteLectura]
    : [];
};

const AsociacionRiesgos = () => {
  const AzureADService = new AADService();
  const token = AzureADService.getToken();
  const [loadingImpactos, setLoadingImpactos] = useState(true);
  const [dataT, setDataT] = useState([]);
  const [impactosNegocio, setImpactosNegocio] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedRiesgo, setSelectedRiesgo] = useState(null);
  const [efectosIniciales, setEfectosIniciales] = React.useState([]);
  const [idEfectosIniciales, setIdEfectosIniciales] = React.useState([]);
  const [efectosFinales, setEfectosFinales] = React.useState([]);
  const [loading, setLoading] = useState(false);
  const [efectoListaTotal, setEfectoListaTotal] = React.useState([]);
  const [activosSeleccionados, setActivosSeleccionados] = React.useState({});
  const [efectoSeleccionado, setEfectoSeleccionado] = useState(null);
  const [modalActivosShow, setModalActivosShow] = React.useState(false);
  const [alcanceCoorporativo, setAlcanceCoorporativo] = React.useState(false);
  const [opcionesActivos, setOpcionesActivos] = React.useState({});
  const [efectosGuardados, setEfectosGuardados] = useState([]);
  const [checkboxStates, setCheckboxStates] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarResultadoOpen, setSnackbarResultadoOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [relacionesAcis, setRelacionesAcis] = useState([]);
  const [contador, setContador] = useState(0);
  const [itemActivo, setItemActivo] = useState(null);
  const [toastConfig, setToastConfig] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success", duration = 3000) => {
    setToastConfig({ show: true, message, type });

    setTimeout(() => {
      setToastConfig((prev) => ({ ...prev, show: false }));
    }, duration);
  };

  const handleCloseSnackbar = () => {
    setSnackbarResultadoOpen(false);
  };

  const generarPayloadPorRiesgo = () => {
    return dataT.reduce((acc, item) => {
      const idriesgo = item.idriesgo;

      // Inicializar estructura si no existe
      if (!acc[idriesgo]) {
        acc[idriesgo] = {
          idriesgo,
          efectos_negocio: { ids: [], truncados: [] },
          efectos_local: { ids: [], truncados: [] },
          efectos_propios: { ids: [] },
        };
      }
      impactosNegocio
        .filter((d) => d.idriesgo === idriesgo && d.estadoasociacion === 1)
        .forEach((d) => {
          acc[idriesgo].efectos_negocio.ids.push(d.idefectonegocio);

          if (
            checkboxStates[d.idefectonegocio] ||
            d.tipoefecto === "Multas y sanciones"
          ) {
            acc[idriesgo].efectos_negocio.truncados.push(d.idefectonegocio);
          }
        });
      getDetallesEfectos(getEfectosPorRiesgo(idriesgo)).forEach((efecto) => {
        const { idefecto, tipoefecto } = efecto;

        if (tipoefecto === "Notificación" || tipoefecto === "Normalización") {
          acc[idriesgo].efectos_propios.ids.push(idefecto);
        } else {
          acc[idriesgo].efectos_local.ids.push(idefecto);

          if (checkboxStates[idefecto] || tipoefecto === "Multas y sanciones") {
            acc[idriesgo].efectos_local.truncados.push(idefecto);
          }
        }
      });

      return acc;
    }, {});
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
    setTimeout(() => {
      setShowTooltip(false);
    }, 5000);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const iconContainerStyle = {
    position: "relative",
    display: "inline-block",
    cursor: "pointer",
    marginLeft: "6px",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    backgroundColor: "#59CBE8",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "14px",
    textAlign: "center",
    lineHeight: "20px",
  };

  const tooltipStyle = {
    position: "absolute",
    bottom: "-45px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#333",
    color: "#fff",
    padding: "6px",
    borderRadius: "4px",
    width: "220px",
    fontSize: "12px",
    opacity: showTooltip ? 1 : 0,
    visibility: showTooltip ? "visible" : "hidden",
    transition: "opacity 0.3s",
    zIndex: 10,
  };

  function ModalActivos({
    show,
    onHide,
    efecto,
    opcionesActivos,
    activosSeleccionados,
    setActivosSeleccionados,
    onAsociar,
    stylesSelect,
  }) {
    return (
      <Modal
        size="xl"
        show={show}
        onHide={onHide}
        aria-labelledby="modal-activos-title"
        centered
        dialogClassName="modal-activos-custom"
      >
        <Modal.Header closeButton>
          <Modal.Title id="modal-activos-title">
            Asociar Efectos a Activos de Información
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Id Efecto</th>
                <th>Fuente</th>
                <th>Tipo Efecto</th>
                <th>Nombre Efecto</th>
                <th>Asociación Activo de Información</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(efecto) && efecto.length > 0 ? (
                efecto
                  .sort((a, b) => {
                    const order = { Negocio: 1, Propio: 2, Local: 3 };
                    return order[a.fuente] - order[b.fuente];
                  })
                  .map((row) => (
                    <tr key={row.idefecto}>
                      <td style={efectosROCellStyle}>{row.idefecto}</td>
                      <td style={efectosROCellStyle}>{row.fuente}</td>
                      <td style={efectosROCellStyle}>{row.tipoefecto}</td>
                      <td style={efectosROCellStyle}>{row.nombreefecto}</td>
                      <td>
                        {!opcionesActivos || opcionesActivos.length === 0 ? (
                          <span style={{ color: "red", fontWeight: "bold" }}>
                            No hay activos Asociados.
                          </span>
                        ) : (
                          <Select
                            isMulti
                            placeholder="Seleccione activos..."
                            styles={stylesSelect}
                            options={opcionesActivos}
                            value={getValueForRow(activosSeleccionados, row)}
                            onChange={(selectedOptions) => {
                              setActivosSeleccionados((prev) => {
                                const id = String(row.idefecto);
                                const grupo = normalizarFuente(row.fuente);
                                const next = {
                                  ...prev,
                                  [id]: { ...(prev[id] || {}) },
                                };
                                if (grupo === "Negocio") {
                                  next[id]["Negocio"] = selectedOptions || [];
                                } else {
                                  next[id]["Local"] = selectedOptions || [];
                                }
                                return next;
                              });
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    style={{ textAlign: "center", color: "gray" }}
                  >
                    No hay datos para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <div className="text-center mt-3">
            <Button
              className="botonPositivo3"
              variant="contained"
              onClick={onAsociar}
            >
              Asociar
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  function MyVerticallyCenteredModal(props) {
    const classes = useStylesModal();
    const [queryText, setqueryText] = React.useState("");
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [inputFields, setInputFields] = React.useState(efectoListaTotal);
    const [page, setPage] = React.useState(0);
    const [selectedIds, setSelectedIds] = React.useState([]);
    useEffect(() => {
      if (props.show) {
        setSelectedIds(props.ids ?? []);
      }
    }, [props.show, props.ids]);
    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    const handleSubmit = (e) => {
      e.preventDefault();

      const idUsuario = localStorage.getItem("idusuario");
      const idRiesgo = props.idriesgo.idriesgo;
      let updatedData = [...props.efectosGuardados];
      updatedData = updatedData.map((item) => {
        if (
          item.idriesgo === idRiesgo &&
          !selectedIds.includes(item.idefecto)
        ) {
          return {
            ...item,
            estadoasociacion: 0,
            fechamodificacion: new Date().toISOString(),
            idusuariomodificacion_id: idUsuario,
          };
        }
        return item;
      });
      selectedIds.forEach((idEfecto) => {
        const existe = updatedData.find(
          (item) => item.idriesgo === idRiesgo && item.idefecto === idEfecto
        );

        if (!existe) {
          updatedData.push({
            idefectoporriesgo: null,
            idriesgo: idRiesgo,
            idefecto: idEfecto,
            fechacreacion: new Date().toISOString(),
            idusuariocreacion_id: idUsuario,
            fechamodificacion: new Date().toISOString(),
            idusuariomodificacion_id: idUsuario,
            estadoasociacion: 1,
            idactivo: "",
          });
        } else if (existe.estadoasociacion === 0) {
          updatedData = updatedData.map((item) => {
            if (item.idriesgo === idRiesgo && item.idefecto === idEfecto) {
              return {
                ...item,
                estadoasociacion: 1,
                fechamodificacion: new Date().toISOString(),
                idusuariomodificacion_id: idUsuario,
              };
            }
            return item;
          });
        }
      });

      if (props.actualizarEfectosTemporales) {
        props.actualizarEfectosTemporales(updatedData);
      }
      setShowModal(false);
    };

    function Item(props) {
      const { sx, ...other } = props;
      return (
        <Box
          sx={{
            textAlign: "center",
            m: 2,
            ...sx,
          }}
          {...other}
        />
      );
    }

    Item.propTypes = {
      sx: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    };

    return (
      <Modal
        {...props}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            Asociar Efectos al Riesgo
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div>
            <Row>
              <Col sm={6} xs={4}>
                <input
                  type="text"
                  className="form-control text-center font-weight-bold"
                  placeholder="Buscar"
                  onChange={(e) => setqueryText(e.target.value)}
                ></input>
              </Col>
            </Row>
            <Paper className={classes.paper}>
              <TableContainer className={classes.container}>
                <Table
                  stickyHeader
                  className={classes.table}
                  aria-labelledby="tableTitle"
                  size={"medium"}
                  aria-label="enhanced table"
                >
                  {/* Inicio de encabezado */}
                  <TableHead className="titulo">
                    <TableRow>
                      <TableCell align="center"></TableCell>
                      <TableCell align="center">Id Efecto</TableCell>
                      <TableCell align="center">Tipo Efecto</TableCell>
                      <TableCell align="center">Nombre Efecto</TableCell>
                      <TableCell align="center">P50</TableCell>
                      <TableCell align="center">P95</TableCell>
                      <TableCell align="center">P99</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inputFields
                      .filter((inputField) =>
                        inputField.idefecto
                          .toString()
                          .toLowerCase()
                          .includes(queryText.toLowerCase())
                      )
                      .sort((a, b) => {
                        const aSelected = selectedIds.includes(a.idefecto);
                        const bSelected = selectedIds.includes(b.idefecto);
                        if (aSelected && !bSelected) return -1;
                        if (!aSelected && bSelected) return 1;
                        return 0;
                      })
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((inputField, index) => {
                        const labelId = `enhanced-table-checkbox-${index}`;
                        const isSelected = selectedIds.includes(
                          inputField.idefecto
                        );
                        const toggleSelection = () => {
                          setSelectedIds((prev) =>
                            prev.includes(inputField.idefecto)
                              ? prev.filter((id) => id !== inputField.idefecto)
                              : [...prev, inputField.idefecto]
                          );
                        };
                        return (
                          <TableRow
                            role="checkbox"
                            tabIndex={-1}
                            key={inputField.idefecto}
                            onClick={toggleSelection}
                            style={{
                              backgroundColor: isSelected
                                ? "rgba(255, 246, 235)"
                                : "transparent",
                              transition: "background-color 0.3s ease",
                              cursor: "pointer",
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                onChange={toggleSelection}
                                inputProps={{ "aria-labelledby": labelId }}
                              />
                            </TableCell>
                            <TableCell>{inputField.idefecto}</TableCell>
                            <TableCell>{inputField.tipoefecto}</TableCell>
                            <TableCell>{inputField.nombreefecto}</TableCell>
                            <TableCell>
                              {formatNumber(inputField.resultado_p50)}
                            </TableCell>
                            <TableCell>
                              {formatNumber(inputField.resultado_p95)}
                            </TableCell>
                            <TableCell>
                              {formatNumber(inputField.resultado_p99)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 20, 30]}
                component="div"
                count={inputFields.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
            <div style={{ paddingTop: "30px" }}>
              <Button
                className="botonPositivo3"
                variant="contained"
                onClick={(event) => {
                  handleSubmit(event);
                }}
              >
                Asociar
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }
  useEffect(() => {
    const fetchdata_lista_efectos = async () => {
      const result = await fetch(
        process.env.REACT_APP_API_URL +
          "efectoslista/0/" +
          localStorage.getItem("idcompania"),
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            // Authorization: "Bearer " + token,
          },
        }
      );
      if (result.status >= 200 && result.status <= 300) {
        let data = await result.json();
        setEfectoListaTotal(data);
      } else {
        setEfectoListaTotal([]);
      }
    };
    const fetch_efectos_acis = async () => {
      const result = await fetch(
        process.env.REACT_APP_API_URL +
          "evaluacion/efectosAcis/" +
          localStorage.getItem("idevaluacion") +
          "/",
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            // Authorization: "Bearer " + token,
          },
        }
      );
      if (result.status >= 200 && result.status <= 300) {
        let data = await result.json();
        setRelacionesAcis(data);
      } else {
        setRelacionesAcis([]);
      }
    };
    const fetch_impactos_negocio = async () => {
      const result = await fetch(
        process.env.REACT_APP_API_URL +
          "evaluacion/impactosNegocio/" +
          localStorage.getItem("idevaluacion") +
          "/",
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );
      if (result.status >= 200 && result.status <= 300) {
        let data = await result.json();
        setLoadingImpactos(false);
        setImpactosNegocio(data);
      } else {
        setLoadingImpactos(false);
        setImpactosNegocio([]);
      }
    };
    const fetchdata = async () => {
      const result = await fetch(
        process.env.REACT_APP_API_URL +
          "evaluacion/newriesgos/" +
          localStorage.getItem("idevaluacion") +
          "/",
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );
      let dataGuardar = await result.json();
      setDataT(dataGuardar);
      let array_nivel_riesgo = [];
      let alcance_corporativo = localStorage.getItem("alcance_corporativo");
      for (let i = 0; i < dataGuardar.length; i++) {
        array_nivel_riesgo.push(dataGuardar[i].nivelriesgo);
      }
      if (
        array_nivel_riesgo.includes("Crítico") ||
        array_nivel_riesgo.includes("Muy Crítico")
      ) {
        if (alcance_corporativo == "1") {
          setAlcanceCoorporativo(true);
        } else {
          setAlcanceCoorporativo(false);
        }
      }
      const resultEfectos = await fetch(
        process.env.REACT_APP_API_URL +
          "evaluacion/newriesgos/efectos/" +
          localStorage.getItem("idevaluacion") +
          "/",
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );
      let efectosInicialesTemp = await resultEfectos.json();
      setIdEfectosIniciales(efectosInicialesTemp);
      if (efectosInicialesTemp.length >= 1) {
        let idsEfecto = [];
        let efectosTemp = [];
        for (let i = 0; i < efectosInicialesTemp.length; i++) {
          if (efectosInicialesTemp[i].estadoasociacion == 1) {
            idsEfecto.push(efectosInicialesTemp[i].idefecto);
            efectosTemp.push({
              ...efectosInicialesTemp[i],
            });
          }
        }
        let datos = JSON.stringify({ id: idsEfecto });
        fetch(process.env.REACT_APP_API_URL + "efectoslista/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            //Authorization: "Bearer " + token,
          },
          body: datos,
        })
          .then((data) =>
            data.json().then((response) => {
              if (data.status >= 200 && data.status < 300) {
                let efectosEnviar = [];
                for (let i = 0; i < efectosTemp.length; i++) {
                  let efectos = [];
                  for (let j = 0; j < response.length; j++) {
                    if (efectosTemp[i].idefecto == response[j].idefecto) {
                      for (let k = 0; k < dataGuardar.length; k++) {
                        if (
                          efectosTemp[i].idriesgo == dataGuardar[k].idriesgo
                        ) {
                          efectos.push(efectosTemp[i].idriesgo, {
                            ...response[j],
                            idactivo: efectosTemp[i].idactivo,
                          });
                          break;
                        }
                      }
                      efectosEnviar.push(efectos);
                      break;
                    }
                  }
                }
                let dictEfectos = [];
                let tempDictEfectos = [];

                for (let i = 0; i < efectosEnviar.length; i++) {
                  if (!tempDictEfectos.includes(efectosEnviar[i][0])) {
                    tempDictEfectos.push(efectosEnviar[i][0]);
                  }
                }
                for (let i = 0; i < tempDictEfectos.length; i++) {
                  let dict = { idriesgo: tempDictEfectos[i] };
                  let arrayTemp = [];
                  for (let j = 0; j < efectosEnviar.length; j++) {
                    if (tempDictEfectos[i] === efectosEnviar[j][0]) {
                      arrayTemp.push(efectosEnviar[j][1]);
                    }
                  }
                  dict["efectos"] = arrayTemp;
                  dictEfectos.push(dict);
                }
                let efectos_finales = [];

                for (let i = 0; i < dictEfectos.length; i++) {
                  let id_efectos_finales = [];

                  for (let j = 0; j < dictEfectos[i].efectos.length; j++) {
                    id_efectos_finales.push(dictEfectos[i].efectos[j].idefecto);
                  }
                  efectos_finales.push({
                    idriesgo: dictEfectos[i].idriesgo,
                    efectos: id_efectos_finales,
                  });
                }
                setEfectosIniciales(dictEfectos);
                setEfectosFinales(efectos_finales);
              } else if (data.status >= 500) {
              } else if (data.status >= 400 && data.status < 500) {
              }
            })
          )
          .catch(function (err) {});
      }
    };

    fetchdata();
    fetch_efectos_acis();
    fetchdata_lista_efectos();
    obtenerClasificacion();
    fetch_impactos_negocio();
  }, []);
  const obtenerClasificacion = async () => {
    const result = await fetch(
      process.env.REACT_APP_API_URL +
        "activoClasificacion/" +
        localStorage.getItem("idactivo") +
        "/",
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + token,
        },
      }
    );

    if (result.ok) {
      let datClasificacion = await result.json();

      const opciones = datClasificacion.flatMap((item) =>
        Array.isArray(item.activos_info)
          ? item.activos_info.map((activo) => ({
              value: activo.idactivo,
              label: activo.activo,
            }))
          : []
      );

      setOpcionesActivos(opciones);
    } else {
      setOpcionesActivos([]);
      return result.status;
    }
  };

  const handleAsociarActivos = useCallback(() => {
    const hoy = new Date().toISOString().slice(0, 10);

    setRelacionesAcis((prevRel) => {
      const nextRel = [...prevRel];
      const keyFor = (idefecto, fuente, idactivo) =>
        `${String(idefecto)}|${normalizarFuente(fuente)}|${String(idactivo)}`;

      const indexMap = new Map();
      for (let i = 0; i < nextRel.length; i++) {
        const r = nextRel[i];
        indexMap.set(keyFor(r.idefecto, r.fuente, r.idactivo), i);
      }
      for (const row of efectoSeleccionado) {
        const idefecto = row.idefecto;
        const fuenteGrupo = normalizarFuente(row.fuente);
        const seleccionUI = getValueForRow(activosSeleccionados, row);
        const seleccionIds = new Set(
          (Array.isArray(seleccionUI) ? seleccionUI : []).map((x) =>
            String(x.value)
          )
        );
        const habilitadosIds = new Set(
          nextRel
            .filter(
              (r) =>
                String(r.idefecto) === String(idefecto) &&
                normalizarFuente(r.fuente) === fuenteGrupo &&
                Number(r.estado) === 1
            )
            .map((r) => String(r.idactivo))
        );
        for (const idActivoStr of seleccionIds) {
          if (!habilitadosIds.has(idActivoStr)) {
            const k = keyFor(idefecto, fuenteGrupo, idActivoStr);
            const idxPrev = indexMap.get(k);
            if (typeof idxPrev === "number") {
              nextRel[idxPrev] = {
                ...nextRel[idxPrev],
                estado: 1,
                fechamodificacion: hoy,
                fuente: fuenteGrupo,
              };
            } else {
              nextRel.push({
                idefectoactivo: null,
                idriesgo_id: selectedRiesgo,
                idefecto: idefecto,
                idactivo: Number(idActivoStr),
                fechamodificacion: hoy,
                estado: 1,
                fuente: fuenteGrupo,
              });
              indexMap.set(k, nextRel.length - 1);
            }
          }
        }
        for (const idActivoStr of habilitadosIds) {
          if (!seleccionIds.has(idActivoStr)) {
            const k = keyFor(idefecto, fuenteGrupo, idActivoStr);
            const idx = indexMap.get(k);
            if (typeof idx === "number") {
              nextRel[idx] = {
                ...nextRel[idx],
                estado: 0,
                fechamodificacion: hoy,
              };
            }
          }
        }
      }

      return nextRel;
    });
    setModalActivosShow(false);
  }, [
    activosSeleccionados,
    efectoSeleccionado,
    setRelacionesAcis,
    selectedRiesgo,
  ]);

  const Toast = ({ message, show, type = "success", duration = 3000 }) => {
    const colors = {
      success: "#4caf50",
      warning: "#ff9800",
      error: "#f44336",
    };

    return (
      <div
        style={{
          position: "fixed",
          top: show ? "50%" : "-200px",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: colors[type],
          color: "white",
          padding: "20px 40px",
          borderRadius: "12px",
          boxShadow: "0px 6px 15px rgba(0,0,0,0.3)",
          transition: "all 0.5s ease-in-out",
          zIndex: 1000,
          fontSize: "20px",
          fontWeight: "bold",
          minWidth: "320px",
          textAlign: "center",
        }}
      >
        {message}
      </div>
    );
  };
  const toggleExpanded = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const openModal = (riesgo) => {
    setSelectedRiesgo(riesgo);
    setShowModal(true);
  };

  const guardarAsociaciones = async (data) => {
    const efectosNegocioActualizados = impactosNegocio.map((efecto) => ({
      ...efecto,
      truncado:
        checkboxStates[efecto.idefectonegocio] ||
        efecto.tipoefecto === "Multas y sanciones"
          ? 1
          : 0,
    }));

    let dataEnviar = [];
    dataEnviar.push({
      riesgos: data,
      efectosnegocio: efectosNegocioActualizados,
      efectoslocales: idEfectosIniciales,
      relacionesacis: relacionesAcis,
      idusuario: localStorage.getItem("idusuario"),
    });
    let dataPUT = JSON.stringify(dataEnviar);
    fetch(
      process.env.REACT_APP_API_URL +
        "evaluacion/newriesgos/" +
        localStorage.getItem("idevaluacion") +
        "/",
      {
        method: "PUT",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          Authorization: "Bearer " + token,
        },
        body: dataPUT,
      }
    )
      .then((data) =>
        data.json().then((response) => {
          if (data.status >= 200 && data.status < 300) {
            showToast(
              "✅ Cambio de relaciones y valoraciones exitoso",
              "success"
            );
          } else if (data.status >= 500) {
            showToast("❌ Error interno del servidor (500)", "error");
          } else if (data.status >= 400 && data.status < 500) {
            showToast("⚠️ Error en la solicitud (400)", "warning");
          }
        })
      )
      .catch(function (err) {});
  };

  const guardarTodasLasAsociaciones = async () => {
    const hayCambios = contador > 0;
    const mensaje = hayCambios
      ? "Se guardarán las relaciones y valoraciones actuales. ¿Desea continuar?"
      : "Antes de guardar, se ejecutará el proceso de cálculo para combinar los efectos y determinar los nuevos niveles de riesgo. ¿Está de acuerdo en continuar?";
    const aceptar = window.confirm(mensaje);
    if (!aceptar) {
      return;
    }
    if (hayCambios) {
      await guardarAsociaciones(dataT);
    } else {
      const nuevaData = await calcular();
      await guardarAsociaciones(nuevaData);
    }
  };

  const calcular = async () => {
    setContador((prev) => prev + 1);
    setSnackbarOpen(true);
    const payload = Object.values(generarPayloadPorRiesgo());
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL
        }evaluacion/calculoImpactos/${localStorage.getItem("idevaluacion")}/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        setSnackbarOpen(false);
        throw new Error("Error en la petición: " + response.status);
      }

      const result = await response.json();
      setSnackbarOpen(false);
      const riesgos = Array.isArray(result) ? result : [];

      if (riesgos.length === 0) {
        setSnackbarOpen(false);
        throw new Error("La respuesta no contiene riesgos");
      }

      // Construir mensaje y actualizar dataT
      let mensaje = "";
      const nuevaData = dataT.map((item) => {
        const nuevo = riesgos.find((r) => r.idriesgo === item.idriesgo);
        if (nuevo) {
          mensaje += `Riesgo ${item.idriesgo}:\n`;
          mensaje += `P50: ${formatNumber(
            item.exposicioninherentep50
          )} → ${formatNumber(nuevo.P50)}\n`;
          mensaje += `P95: ${formatNumber(
            item.exposicioninherentep95
          )} → ${formatNumber(nuevo.P95)}\n`;
          mensaje += `P99: ${formatNumber(
            item.exposicioninherentep99
          )} → ${formatNumber(nuevo.P99)}\n`;
          mensaje += `Residual: ${item.exposicionresidual} → ${formatNumber(
            nuevo.riesgo_residual
          )}\n`;
          mensaje += `Nivel: ${item.nivelriesgo} → ${nuevo.nivelriesgo}\n\n`;

          return {
            ...item,
            exposicioninherentep50: nuevo.P50,
            exposicioninherentep95: nuevo.P95,
            exposicioninherentep99: nuevo.P99,
            exposicionresidual: parseFloat(nuevo.riesgo_residual),
            nivelriesgo: nuevo.nivelriesgo,
          };
        }
        return item;
      });

      setSnackbarMessage(mensaje);
      setSnackbarResultadoOpen(true);
      setDataT(nuevaData);
      return nuevaData;
    } catch (error) {
      setSnackbarOpen(false);
      alert(error.message);
    }
  };

  const getImpactosLocalesPorRiesgo = (riesgoId) => {
    // Filtra todos los objetos que tengan el idriesgo igual al riesgoId
    const relaciones = idEfectosIniciales.filter(
      (item) => item.idriesgo === riesgoId
    );

    // Si no hay relaciones, devuelve un arreglo vacío
    if (relaciones.length === 0) return [];

    // Devuelve un arreglo con todos los idefecto asociados
    return relaciones.map((item) => item.idefecto);
  };

  const getEfectosPorRiesgo = (idriesgo) => {
    return idEfectosIniciales.filter(
      (efecto) => efecto.idriesgo === idriesgo && efecto.estadoasociacion === 1
    );
  };

  const getDetallesEfectos = (listaEfectos) => {
    const ids = listaEfectos.map((e) => e.idefecto);
    return efectoListaTotal.filter((detalle) => ids.includes(detalle.idefecto));
  };
  const hayAsociaciones = idEfectosIniciales.some(
    (impacto) => impacto.idefectoporriesgo && impacto.idefectoporriesgo > 0
  );
  // Iconos SVG
  const ChevronDownIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="6,9 12,15 18,9"></polyline>
    </svg>
  );
  const ChevronUpIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="18,15 12,9 6,15"></polyline>
    </svg>
  );
  const PlusIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );
  const SaveIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17,21 17,13 7,13 7,21" />
      <polyline points="7,3 7,8 15,8" />
    </svg>
  );
  const CalculateIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      {/* Marco del ícono */}
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      {/* Símbolos de suma y resta */}
      <line x1="8" y1="8" x2="12" y2="8" /> {/* Línea horizontal para resta */}
      <line x1="8" y1="12" x2="12" y2="12" /> {/* Otra línea horizontal */}
      <line x1="16" y1="10" x2="16" y2="14" /> {/* Línea vertical para suma */}
      <line x1="14" y1="12" x2="18" y2="12" />{" "}
      {/* Línea horizontal para suma */}
    </svg>
  );
  const LoadingIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      xmlns="http://www.w3.org/2000/svg"
      style={{ animation: "spin 1s linear infinite" }}
    >
      <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" />
      <style>
        {`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}
      </style>
    </svg>
  );
  const AlertTriangleIcon = () => (
    <svg
      width="64"
      height="64"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <path d="M12 9v4" />
      <path d="m12 17 .01 0" />
    </svg>
  );

  const DeleteIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="3,6 5,6 21,6"></polyline>
      <path d="m19,6 v14 a2,2 0 0,1 -2,2 H7 a2,2 0 0,1 -2,-2 V6 m3,0 V4 a2,2 0 0,1 2,-2 h4 a2,2 0 0,1 2,2 v2"></path>
      <line x1="10" y1="11" x2="10" y2="17"></line>
      <line x1="14" y1="11" x2="14" y2="17"></line>
    </svg>
  );

  // Estilos
  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "100vh",
    background: "white",
    padding: "24px",
  };

  const mainContentStyle = {
    width: "100%",
  };

  const headerStyle = {
    textAlign: "center",
    marginBottom: "32px",
    position: "relative",
  };

  const titleStyle = {
    fontSize: "2.25rem",
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: "8px",
  };

  const decorativeLineStyle = {
    width: "96px",
    height: "4px",
    background: "#FDDA24",
    margin: "0 auto",
    borderRadius: "2px",
  };

  const subtitleStyle = {
    color: "#6b7280",
    marginTop: "16px",
  };

  const loadingContainerStyle = {
    position: "absolute",
    top: "0",
    right: "60px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontSize: "1.25rem",
    color: "#6b7280",
  };

  const globalSaveButtonStyle = {
    backgroundColor: "#00C389",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  };
  const globalCalculateButtonStyle = {
    backgroundColor: "#9063CD",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  };
  const cardStyle = {
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    border: "1px solid #f3f4f6",
    overflow: "hidden",
    marginBottom: "16px",
    transition: "all 0.3s ease",
  };

  const headerClickableStyle = {
    cursor: "pointer",
    transition: "all 0.2s ease",
    padding: "24px",
  };

  const headerContentStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const leftContentStyle = {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flex: 1,
  };

  const idBadgeStyle = {
    background: "#FDDA24",
    color: "white",
    padding: "8px 16px",
    borderRadius: "8px",
    fontWeight: "600",
    fontSize: "0.875rem",
    minWidth: "80px",
    textAlign: "center",
  };

  const eventTitleStyle = {
    fontSize: "1.125rem",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: "4px",
  };

  const expandIconStyle = {
    marginLeft: "16px",
    padding: "8px",
    borderRadius: "50%",
    transition: "background-color 0.2s ease",
    color: "#6b7280",
  };

  const expandedContentStyle = {
    borderTop: "1px solid #f3f4f6",
    backgroundColor: "#f9fafb",
    padding: "24px",
  };

  const addButtonStyle = {
    backgroundColor: "#59CBE8",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "background-color 0.2s ease",
    marginBottom: "16px",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "white",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    marginBottom: "16px",
  };

  const tableHeaderStyle = {
    backgroundColor: "#f8fafc",
    padding: "12px",
    textAlign: "left",
    fontWeight: "600",
    color: "#374151",
    borderBottom: "1px solid #e5e7eb",
  };

  const tableCellStyle = {
    padding: "12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#6b7280",
  };

  const emptyStateStyle = {
    textAlign: "center",
    padding: "48px 0",
  };

  const noAsociacionesStyle = {
    textAlign: "center",
    padding: "24px",
    color: "#6b7280",
    fontStyle: "italic",
    backgroundColor: "#f9fafb",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
  };

  const efectosROCellStyle = {
    ...tableCellStyle,
    backgroundColor: "#0000",
    fontWeight: "600",
    color: "#92400e",
  };

  const deleteButtonStyle = {
    backgroundColor: "#dc2626",
    color: "white",
    border: "none",
    borderRadius: "6px",
    padding: "8px",
    fontSize: "0.75rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s ease",
    width: "32px",
    height: "32px",
  };

  const restoreButtonStyle = {
    backgroundColor: "#16a34a", // Verde
    color: "white",
    border: "none",
    borderRadius: "6px",
    padding: "8px",
    fontSize: "0.75rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s ease",
    width: "32px",
    height: "32px",
  };
  useEffect(() => {
    const initialStates = {};
    impactosNegocio.forEach((dato) => {
      initialStates[dato.idefectonegocio] = dato.truncado === 1; // true si truncado = 1
    });
    setCheckboxStates(initialStates);
  }, [impactosNegocio]);

  useEffect(() => {
    if (!modalActivosShow) return;
    const data = agruparActivosPorEfectoYFuente({
      item: itemActivo,
      opcionesActivos,
      relacionesAcis,
      resultadoFinal: efectoSeleccionado,
    });
    setActivosSeleccionados(data);
  }, [
    modalActivosShow,
    relacionesAcis,
    opcionesActivos,
    efectoSeleccionado,
    itemActivo,
  ]);

  return (
    <>
      {loading ? (
        <div style={{ textAlign: "center", padding: "16px" }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            style={{ margin: "auto", background: "none", display: "block" }}
            width="50px"
            height="50px"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid"
          >
            <circle
              cx="50"
              cy="50"
              fill="none"
              stroke="#facc15" // amarillo
              strokeWidth="10"
              r="35"
              strokeDasharray="164.93361431346415 56.97787143782138"
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                repeatCount="indefinite"
                dur="1s"
                values="0 50 50;360 50 50"
                keyTimes="0;1"
              />
            </circle>
          </svg>
          <p>Cargando Efectos por riesgo...</p>
        </div>
      ) : (
        <div style={containerStyle}>
          <div style={mainContentStyle}>
            <div style={headerStyle}>
              <h1 style={titleStyle}>Asociación Impactos - Riesgos</h1>
              <div style={decorativeLineStyle}></div>
              <p style={subtitleStyle}>
                Impactos propios e impactos materializados en el negocio
              </p>
              {loadingImpactos && (
                <div style={loadingContainerStyle}>
                  <LoadingIcon />
                  <span>Impactos del negocio</span>
                </div>
              )}
            </div>
            <div
              style={{
                ...headerStyle,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* <>
                {hayAsociaciones && (
                  <button
                    style={globalSaveButtonStyle}
                    onClick={guardarTodasLasAsociaciones}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#047857";
                      e.target.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#00C389";
                      e.target.style.transform = "translateY(0px)";
                    }}
                  >
                    <SaveIcon />
                    Guardar todas las asociaciones
                  </button>
                )}
              </>

              {/* Botón Calcular a la derecha */}
              {/* <button
                style={{
                  ...globalCalculateButtonStyle,
                  marginLeft: "10px", // opcional para separación
                }}
                onClick={calcular}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#7A4FB8"; // tono más oscuro
                  e.target.style.transform = "scale(1.05)"; // efecto de agrandar
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "#9063CD"; // vuelve al original
                  e.target.style.transform = "scale(1)"; // tamaño normal
                }}
              >
                <CalculateIcon />
                Calcular
              </button> */}
            </div>
            <div>
              {dataT.map((item, index) => (
                <div key={index} style={cardStyle}>
                  {/* Header del acordeón */}
                  <div
                    style={headerClickableStyle}
                    onClick={() => toggleExpanded(index)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <div style={headerContentStyle}>
                      <div style={leftContentStyle}>
                        <div style={idBadgeStyle}>{item.idriesgo}</div>
                        <div style={{ flex: 1 }}>
                          <h3 style={eventTitleStyle}>{item.evento}</h3>
                        </div>
                        <div style={{ marginTop: "10px" }}>
                          <div>
                            <strong>Exposición residual:</strong>{" "}
                            {formatNumber(item.exposicionresidual)}
                          </div>
                          <div>
                            <strong>Nivel de Riesgo:</strong>{" "}
                            <span
                              style={{ color: getRiskColor(item.nivelriesgo) }}
                            >
                              {item.nivelriesgo}
                            </span>
                          </div>
                          <div>
                            {}
                            <strong>Mitigación:</strong>{" "}
                            <span
                              style={{
                                color: getGradientColor(
                                  getMitigacionValue(item)
                                ),
                                fontWeight: "bold",
                              }}
                            >
                              {(getMitigacionValue(item) * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      <div style={expandIconStyle}>
                        {expandedItems[index] ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Contenido expandido */}
                  {expandedItems[index] && (
                    <div style={expandedContentStyle}>
                      {/* Botón para abrir modal */}
                      <button
                        style={addButtonStyle}
                        onClick={() => openModal(item)}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#9063CD";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#59CBE8";
                        }}
                      >
                        <PlusIcon />
                        Asociar Efectos - Riesgos
                      </button>
                      {getImpactosLocalesPorRiesgo(item.idriesgo).length > 0 ||
                      impactosNegocio.filter(
                        (dato) =>
                          dato.idriesgo === item.idriesgo &&
                          dato.estadoasociacion === 1
                      ).length > 0 ? (
                        <div style={{ marginBottom: "24px" }}>
                          <table style={tableStyle}>
                            <thead>
                              <tr>
                                <th style={tableHeaderStyle}>ID efecto</th>
                                <th style={tableHeaderStyle}>Fuente</th>
                                <th style={tableHeaderStyle}>Tipo efecto</th>
                                <th style={tableHeaderStyle}>P50</th>
                                <th style={tableHeaderStyle}>P95</th>
                                <th style={tableHeaderStyle}>P99</th>
                                <th style={tableHeaderStyle}>
                                  Frecuencia limitada{" "}
                                  <span
                                    style={iconContainerStyle}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                  >
                                    ?
                                    <span style={tooltipStyle}>
                                      Indicar si el efecto debe tener una
                                      frecuencia de máximo 1 ocurrencia al año
                                    </span>
                                  </span>
                                </th>

                                <th
                                  style={{
                                    ...tableHeaderStyle,
                                    width: "80px",
                                    textAlign: "center",
                                  }}
                                ></th>
                              </tr>
                            </thead>
                            <tbody>
                              {[...impactosNegocio]
                                .sort(
                                  (a, b) =>
                                    b.estadoasociacion - a.estadoasociacion
                                )
                                .filter(
                                  (dato) => dato.idriesgo === item.idriesgo
                                )
                                .map((dato) => (
                                  <tr
                                    key={dato.idefectonegocioporriesgo}
                                    style={{
                                      backgroundColor: "#f3e8ff",
                                      textDecoration:
                                        dato.estadoasociacion === 0
                                          ? "line-through"
                                          : "none",
                                      opacity:
                                        dato.estadoasociacion === 0 ? 0.6 : 1,
                                    }}
                                  >
                                    <td style={efectosROCellStyle}>
                                      {dato.idefectonegocio}
                                    </td>
                                    <td style={efectosROCellStyle}>Negocio</td>
                                    <td style={efectosROCellStyle}>
                                      {dato.tipoefecto}
                                    </td>
                                    <td style={efectosROCellStyle}>
                                      {formatNumber(dato.resultado_p50)}
                                    </td>
                                    <td style={efectosROCellStyle}>
                                      {formatNumber(dato.resultado_p95)}
                                    </td>
                                    <td style={efectosROCellStyle}>
                                      {formatNumber(dato.resultado_p99)}
                                    </td>
                                    <td style={efectosROCellStyle}>
                                      <input
                                        type="checkbox"
                                        checked={
                                          checkboxStates[
                                            dato.idefectonegocio
                                          ] ||
                                          dato.tipoefecto ===
                                            "Multas y sanciones" ||
                                          false
                                        }
                                        onChange={(e) =>
                                          setCheckboxStates((prev) => ({
                                            ...prev,
                                            [dato.idefectonegocio]:
                                              e.target.checked,
                                          }))
                                        }
                                        disabled={
                                          dato.tipoefecto ===
                                          "Multas y sanciones"
                                        }
                                      />
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        textAlign: "center",
                                        padding: "8px",
                                      }}
                                    >
                                      <>
                                        <button
                                          style={
                                            dato.estadoasociacion === 1
                                              ? deleteButtonStyle
                                              : restoreButtonStyle
                                          }
                                          onClick={() => {
                                            // Actualizar estadoasociacion
                                            setImpactosNegocio((prev) =>
                                              prev.map((item) =>
                                                item.idefectonegocioporriesgo ===
                                                dato.idefectonegocioporriesgo
                                                  ? {
                                                      ...item,
                                                      estadoasociacion:
                                                        item.estadoasociacion ===
                                                        1
                                                          ? 0
                                                          : 1,
                                                    }
                                                  : item
                                              )
                                            );
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.backgroundColor =
                                              dato.estadoasociacion === 1
                                                ? "#b91c1c"
                                                : "#15803d";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.backgroundColor =
                                              dato.estadoasociacion === 1
                                                ? "#dc2626"
                                                : "#16a34a";
                                          }}
                                          title={
                                            dato.estadoasociacion === 1
                                              ? "Eliminar asociación"
                                              : "Reintegrar asociación"
                                          }
                                        >
                                          {dato.estadoasociacion === 1 ? (
                                            <DeleteIcon />
                                          ) : (
                                            <RestoreIcon />
                                          )}
                                        </button>
                                      </>
                                    </td>
                                  </tr>
                                ))}
                              {getDetallesEfectos(
                                getEfectosPorRiesgo(item.idriesgo)
                              )
                                // 1. Ordenamos según la prioridad
                                .sort((a, b) => {
                                  const prioridad = {
                                    Notificacion: 1,
                                    Normalización: 2,
                                  };
                                  const prioridadA =
                                    prioridad[a.tipoefecto] || 99;
                                  const prioridadB =
                                    prioridad[b.tipoefecto] || 99;
                                  return prioridadA - prioridadB;
                                })
                                .map((efecto, efectoIndex) => {
                                  const isEspecial =
                                    efecto.tipoefecto === "Notificación" ||
                                    efecto.tipoefecto === "Normalización";
                                  return (
                                    <tr
                                      key={efectoIndex}
                                      style={{
                                        backgroundColor: isEspecial
                                          ? "#d1fae5"
                                          : "transparent", // verde pastel
                                      }}
                                    >
                                      <td style={efectosROCellStyle}>
                                        {efecto.idefecto}
                                      </td>
                                      <td style={efectosROCellStyle}>
                                        {isEspecial ? "Propio" : "Local"}
                                      </td>
                                      <td style={efectosROCellStyle}>
                                        {efecto.tipoefecto}
                                      </td>
                                      <td style={efectosROCellStyle}>
                                        {formatNumber(efecto.resultado_p50)}
                                      </td>
                                      <td style={efectosROCellStyle}>
                                        {formatNumber(efecto.resultado_p95)}
                                      </td>
                                      <td style={efectosROCellStyle}>
                                        {formatNumber(efecto.resultado_p99)}
                                      </td>
                                      <td style={efectosROCellStyle}></td>
                                      <td style={efectosROCellStyle}></td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center", // Alinea verticalmente
                              gap: 15, // Espacio entre el botón y las leyendas
                            }}
                          >
                            {/* Botón */}
                            <Button
                              style={addButtonStyle}
                              className="botonpositivo3"
                              onClick={() => {
                                const efectosOriginales = getDetallesEfectos(
                                  getEfectosPorRiesgo(item.idriesgo)
                                );
                                const procesadaBase1 = efectosOriginales.map(
                                  (dato) => ({
                                    idefecto: dato.idefecto,
                                    tipoefecto: dato.tipoefecto,
                                    nombreefecto: dato.nombreefecto,
                                    idactivo: dato.idactivo || "",
                                    fuente:
                                      dato.tipoefecto === "Notificación" ||
                                      dato.tipoefecto === "Normalización"
                                        ? "Propio"
                                        : "Local",
                                  })
                                );
                                const procesadaBase2 = impactosNegocio
                                  .filter(
                                    (dato) =>
                                      dato.idriesgo === item.idriesgo &&
                                      dato.estadoasociacion === 1
                                  )
                                  .map((dato) => ({
                                    idefecto: dato.idefecto,
                                    tipoefecto: dato.tipoefecto,
                                    nombreefecto: dato.nombreefecto,
                                    idactivo: "",
                                    fuente: "Negocio",
                                  }));
                                const resultadoFinal = [
                                  ...procesadaBase1,
                                  ...procesadaBase2,
                                ];
                                setEfectoSeleccionado(resultadoFinal);
                                setItemActivo(item);
                                setModalActivosShow(true);
                                setSelectedRiesgo(item.idriesgo);
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = "#9063CD";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = "#59CBE8";
                              }}
                            >
                              <PlusIcon />
                              Asociar Efectos - Activos de Información
                            </Button>
                            <Box sx={{ display: "flex", gap: 2 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    backgroundColor: "#f3e8ff",
                                    border: "1px solid grey",
                                    borderRadius: "4px",
                                  }}
                                />
                                <span>
                                  Impactos que se materializados en el negocio
                                </span>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    backgroundColor: "#d1fae5",
                                    border: "1px solid grey",
                                    borderRadius: "4px",
                                  }}
                                />
                                <span>Impactos propios</span>
                              </Box>
                            </Box>
                          </Box>
                        </div>
                      ) : (
                        <div style={noAsociacionesStyle}>
                          <p>No hay efectos asociados al riesgo</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {dataT.length === 0 && (
              <div style={emptyStateStyle}>
                <div style={{ color: "#9ca3af", marginBottom: "16px" }}>
                  <AlertTriangleIcon />
                </div>
                <h3
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "600",
                    color: "#6b7280",
                    marginBottom: "8px",
                  }}
                >
                  No hay riesgos registrados
                </h3>
                <p style={{ color: "#9ca3af" }}>
                  Los riesgos identificados aparecerán aquí
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {showModal && (
        <MyVerticallyCenteredModal
          show={showModal}
          onHide={() => setShowModal(false)}
          idriesgo={selectedRiesgo}
          efectosGuardados={idEfectosIniciales}
          ids={getEfectosPorRiesgo(selectedRiesgo?.idriesgo || 0).map(
            (e) => e.idefecto
          )}
          actualizarEfectosTemporales={(nuevoJson) =>
            setIdEfectosIniciales(nuevoJson)
          }
        />
      )}
      <ModalActivos
        show={modalActivosShow}
        onHide={() => setModalActivosShow(false)}
        efecto={efectoSeleccionado}
        opcionesActivos={opcionesActivos}
        activosSeleccionados={activosSeleccionados}
        setActivosSeleccionados={setActivosSeleccionados}
        onAsociar={handleAsociarActivos}
        stylesSelect={stylesSelect}
      />
      <Snackbar
        open={snackbarOpen}
        message="Calculando valoraciones de los riesgos con las relaciones actuales (aprox 20s)"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setSnackbarOpen(false)}
        ContentProps={{
          sx: {
            fontSize: "1.2rem",
            padding: "20px",
            minWidth: "400px",
            justifyContent: "center",
            backgroundColor: "#333", // opcional para estilo
          },
        }}
      />

      <Snackbar
        open={snackbarResultadoOpen}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        ContentProps={{
          sx: {
            backgroundColor: "#d1fae5",
            color: "#065f46",
            fontSize: "1.2rem",
            fontWeight: "500",
            padding: "24px 32px",
            minWidth: "420px",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            whiteSpace: "pre-line",
          },
        }}
        message={snackbarMessage}
      />
      <Toast
        show={toastConfig.show}
        message={toastConfig.message}
        type={toastConfig.type}
      />
    </>
  );
};

export default AsociacionRiesgos;
